import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../environments/environment";
import { TipoSimulacao } from "../../enums/simulation-type.enum";
import { CaqRequestService } from "../caq-request/caq-request.service";
import { LocalizacaoService } from "../localizacao/localizacao.service";
import { NumeroFuncionariosDto } from "../../dtos/numero-funcionarios.dto";

export interface NumeroFuncionariosResponseDto {
    content: NumeroFuncionariosDto;
}

@Injectable({ providedIn: 'root' })
export class NumeroFuncionariosService {
    private apiUrl = `${environment.apiUrl}/numero-funcionarios`;

    constructor(
        private caqRequestService: CaqRequestService,
        private localizacaoService: LocalizacaoService
    ) { }

    async obterPorLocalizacao(): Promise<NumeroFuncionariosDto> {
        const url = this.construirUrlNumeroFuncionarios();
        const response = await firstValueFrom(
            this.caqRequestService.get<NumeroFuncionariosResponseDto>(url)
        );
        return response.content;
    }

    async obterPlanoMunicipalNacional(): Promise<NumeroFuncionariosDto> {
        const response = await firstValueFrom(
            this.caqRequestService.get<NumeroFuncionariosResponseDto>(`${this.apiUrl}/plano-municipal`)
        );
        return response.content;
    }

    async obterRedeMunicipalNacional(): Promise<NumeroFuncionariosDto> {
        const response = await firstValueFrom(
            this.caqRequestService.get<NumeroFuncionariosResponseDto>(`${this.apiUrl}/rede-municipal`)
        );
        return response.content;
    }

    async obterPlanoMunicipalPorEstado(codUf: number | string): Promise<NumeroFuncionariosDto> {
        const response = await firstValueFrom(
            this.caqRequestService.get<NumeroFuncionariosResponseDto>(`${this.apiUrl}/plano-municipal/estado/${codUf}`)
        );
        return response.content;
    }

    async obterRedeMunicipalPorEstado(codUf: number | string): Promise<NumeroFuncionariosDto> {
        const response = await firstValueFrom(
            this.caqRequestService.get<NumeroFuncionariosResponseDto>(`${this.apiUrl}/rede-municipal/estado/${codUf}`)
        );
        return response.content;
    }

    async obterPlanoMunicipalPorMunicipio(codMun: number | string): Promise<NumeroFuncionariosDto> {
        const response = await firstValueFrom(
            this.caqRequestService.get<NumeroFuncionariosResponseDto>(`${this.apiUrl}/plano-municipal/municipio/${codMun}`)
        );
        return response.content;
    }

    async obterRedeMunicipalPorMunicipio(codMun: number | string): Promise<NumeroFuncionariosDto> {
        const response = await firstValueFrom(
            this.caqRequestService.get<NumeroFuncionariosResponseDto>(`${this.apiUrl}/rede-municipal/municipio/${codMun}`)
        );
        return response.content;
    }

    private construirUrlNumeroFuncionarios(): string {
        const localizacao = this.localizacaoService.obterLocalizacaoSelecionada();

        if (!localizacao) {
            return `${this.apiUrl}/plano-municipal`;
        }

        const tipo = localizacao.tipoSimulacaoSelecionado;

        if (tipo === TipoSimulacao.planejamentoNacional) {
            return `${this.apiUrl}/plano-municipal`;
        }

        if (localizacao.cidadeSelecionada) {
            const codMun = localizacao.cidadeSelecionada.value;
            if (tipo === TipoSimulacao.planejamento) {
                return `${this.apiUrl}/plano-municipal/municipio/${codMun}`;
            }
            if (tipo === TipoSimulacao.planejamentoPorEsferaAdmMunicipal) {
                return `${this.apiUrl}/rede-municipal/municipio/${codMun}`;
            }
        }

        if (localizacao.estadoSelecionado) {
            const codUf = localizacao.estadoSelecionado.value;
            if (tipo === TipoSimulacao.planejamento) {
                return `${this.apiUrl}/plano-municipal/estado/${codUf}`;
            }
            if (tipo === TipoSimulacao.planejamentoPorEsferaAdmEstadual) {
                return `${this.apiUrl}/rede-municipal/estado/${codUf}`;
            }
        }

        throw new Error('Tipo de simulação ou localização inválida para Número de Funcionários');
    }
}